/* Half-Life-Sprites (.spr, IDSP Version 2): lesen, erstes Bild als RGBA. Keine DOM-Abhängigkeit. */
import I18n from '../i18n.ts';
export interface SprFrame { w: number; h: number; rgba: Uint8ClampedArray }
export interface SprFile {
  type: number; typeName: string; format: number; formatName: string; radius: number;
  w: number; h: number; maxW: number; maxH: number; count: number; frames: SprFrame[]; rgba: Uint8ClampedArray;
}

const Spr = (function () {
  'use strict';
  const _ = I18n.t;
  const FORMATS = ['normal', 'spr.f_additive', 'index-alpha', 'alphatest'];
  const TYPES = ['spr.t_parallel_upright', 'spr.t_facing_upright', 'spr.t_parallel', 'spr.t_oriented', 'spr.t_parallel_oriented'];
  /** buf: ArrayBuffer -> { type, format, formatName, radius, w, h, frames, rgba(erstes Bild) } */
  function parse(buf: ArrayBuffer): SprFile {
    const dv = new DataView(buf), u8 = new Uint8Array(buf);
    if (buf.byteLength < 48 || dv.getUint32(0, true) !== 0x50534449) throw new Error(_('spr.e_not'));
    const ver = dv.getInt32(4, true);
    if (ver !== 2) throw new Error(_('spr.e_version', { ver: ver }));
    const type = dv.getInt32(8, true), format = dv.getInt32(12, true), radius = dv.getFloat32(16, true);
    const maxW = dv.getInt32(20, true), maxH = dv.getInt32(24, true), n = dv.getInt32(28, true);
    let o = 40;
    const colors = dv.getInt16(o, true); o += 2;
    if (colors < 1 || colors > 256 || o + colors * 3 > buf.byteLength) throw new Error(_('spr.e_palette'));
    const pal = u8.subarray(o, o + colors * 3); o += colors * 3;
    const frames: SprFrame[] = [];
    const readFrame = (): SprFrame => {
      const w = dv.getInt32(o + 8, true), h = dv.getInt32(o + 12, true); o += 16;
      if (w <= 0 || h <= 0 || w > 2048 || h > 2048 || o + w * h > buf.byteLength) throw new Error(_('spr.e_image'));
      const idx = u8.subarray(o, o + w * h); o += w * h;
      const rgba = new Uint8ClampedArray(w * h * 4);
      for (let i = 0; i < w * h; i++) {
        const p = idx[i] * 3, k = i * 4;
        if (format === 2) { rgba[k] = pal[colors * 3 - 3]; rgba[k + 1] = pal[colors * 3 - 2]; rgba[k + 2] = pal[colors * 3 - 1]; rgba[k + 3] = idx[i]; }
        else { rgba[k] = pal[p]; rgba[k + 1] = pal[p + 1]; rgba[k + 2] = pal[p + 2]; rgba[k + 3] = format === 3 && idx[i] === 255 ? 0 : 255; }
      }
      return { w: w, h: h, rgba: rgba };
    };
    for (let i = 0; i < Math.min(n, 64) && o + 4 <= buf.byteLength; i++) {
      const group = dv.getInt32(o, true); o += 4;
      if (group === 0) frames.push(readFrame());
      else { const gn = dv.getInt32(o, true); o += 4 + gn * 4; for (let g = 0; g < gn; g++) frames.push(readFrame()); }
    }
    if (!frames.length) throw new Error(_('spr.e_empty'));
    const f0 = frames[0];
    return { type: type, typeName: TYPES[type] ? (TYPES[type].indexOf('spr.') === 0 ? _(TYPES[type]) : TYPES[type]) : String(type), format: format, formatName: FORMATS[format] ? (FORMATS[format].indexOf('spr.') === 0 ? _(FORMATS[format]) : FORMATS[format]) : String(format), radius: radius, w: f0.w, h: f0.h, maxW: maxW, maxH: maxH, count: n, frames: frames, rgba: f0.rgba };
  }
  /** Schlüssel für einen model-Wert wie "sprites/glow01.spr": kleingeschriebener Dateiname */
  const key = (model: unknown): string => String(model || '').split(/[\\/]/).pop()!.toLowerCase();
  return { parse, key };
})();
export default Spr;
